import { DataFieldServicesContext, IDataFieldServices, useDataFieldServices } from "@iventis/datafield-editor";
import { stripStyleValuesWithRemovedDataFieldReferences, stripStyleValuesWithRemovedListItemReference } from "@iventis/map-engine/src/utilities/style-helpers";
import React, { FunctionComponent, ReactNode, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { patchMapLayers } from "./state/map.slice.thunks";
import { RootDispatch, RootState } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";

/** Wrap this component around the data field modal in the sidebar so it can intercept any removed data field/list items and update the layer accordingly */
export const SidebarDataFieldModalWrapper: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
    const dispatch = useDispatch<RootDispatch>();
    const { dataFieldsService, dataFieldListItemsService } = useDataFieldServices();
    const layers = useSelector((s: RootState) => s.mapReducer.mapModule.layers.value);

    // Intercept the data field services to ensure the layer is up to date
    const dataFieldServices: IDataFieldServices = useMemo(
        () => ({
            dataFieldsService: {
                ...dataFieldsService,
                deleteDataField: async (dataFieldId, layerId) => {
                    // Delete the data field
                    await dataFieldsService.deleteDataField(dataFieldId, layerId);
                    // Strip the references to that data field in its' layer
                    const layer = stripStyleValuesWithRemovedDataFieldReferences(
                        layers.find((l) => l.id === layerId),
                        dataFieldId
                    );
                    // Remove the data field from the layer
                    layer.dataFields = [...layer.dataFields.filter((df) => df.id !== dataFieldId)];
                    // Update the layer preview
                    dispatch(sidebar.actions.updateLayerSnapshot([layer]));
                    // Update the layer via patch
                    await dispatch(
                        patchMapLayers([
                            {
                                id: layer.id,
                                areaStyle: layer.areaStyle,
                                lineStyle: layer.lineStyle,
                                pointStyle: layer.pointStyle,
                                iconStyle: layer.iconStyle,
                                modelStyle: layer.modelStyle,
                                lineModelStyle: layer.lineModelStyle,
                                areaModelStyle: layer.areaModelStyle,
                                tooltipDataFieldIds: layer.tooltipDataFieldIds,
                            },
                        ])
                    );
                },
            },
            dataFieldListItemsService: {
                ...dataFieldListItemsService,
                deleteDataFieldListItem: async (listItemId, dataFieldId, layerId) => {
                    // Delete the data field list item
                    await dataFieldListItemsService.deleteDataFieldListItem(listItemId, dataFieldId, layerId);
                    // Strip the references to that list item in its' layer
                    const layer = stripStyleValuesWithRemovedListItemReference(
                        layers.find((l) => l.id === layerId),
                        dataFieldId
                    );
                    // Update the layer preview
                    dispatch(sidebar.actions.updateLayerSnapshot([layer]));
                    // Update the layer via patch
                    await dispatch(
                        patchMapLayers([
                            {
                                id: layer.id,
                                areaStyle: layer.areaStyle,
                                lineStyle: layer.lineStyle,
                                pointStyle: layer.pointStyle,
                                iconStyle: layer.iconStyle,
                                modelStyle: layer.modelStyle,
                                lineModelStyle: layer.lineModelStyle,
                                areaModelStyle: layer.areaModelStyle,
                                tooltipDataFieldIds: layer.tooltipDataFieldIds,
                            },
                        ])
                    );
                },
            },
        }),
        [dataFieldsService, dataFieldListItemsService, layers]
    );
    return <DataFieldServicesContext.Provider value={dataFieldServices}>{children}</DataFieldServicesContext.Provider>;
};
